<template>
    <StandardLayout @sidebar-toggle="sidebarToggle()" :sidebarNarrow="true" :clipStations="true">
        <div class="project-public project-container" :class="{ 'winter-banner-visible': showWinterBanner }" v-if="displayProject">
            <ProjectDetailCard :project="project"></ProjectDetailCard>

            <div v-if="showWinterBanner" class="floodnet-winter-banner">
                {{ $t("map.winterBanner") }}
            </div>

            <template v-if="viewType === 'list'">
                <div class="stations-list" v-if="projectStations && projectStations.length > 0">
                    <div v-for="station in projectStations" v-bind:key="station.id">
                        <StationHoverSummary
                            class="summary-container"
                            :station="station"
                            :sensorDataQuerier="sensorDataQuerier"
                            :exploreContext="exploreContext"
                            :visibleReadings="visibleReadings"
                            v-slot="{ sensorDataQuerier }"
                        >
                            <TinyChart
                                v-if="!isRetiredStation(station)"
                                :station-id="station.id"
                                :station="station"
                                :querier="sensorDataQuerier"
                            />
                        </StationHoverSummary>
                    </div>
                </div>
            </template>

            <template v-if="viewType === 'map'">
                <div class="map-legend" v-if="isPartnerCustomisationEnabled" :class="{ collapsed: legendCollapsed }">
                    <a class="legend-toggle" @click="legendCollapsed = !legendCollapsed">
                        <i class="icon icon-chevron-right"></i>
                    </a>
                    <div class="legend-content">
                        <div class="legend-content-wrap">
                            <h4>{{ keyTitle }}</h4>
                            <div class="legend-item" v-for="(item, idx) in levels" :key="idx">
                                <div class="legend-item-icon">
                                    <span class="legend-dot" :style="{ color: item.color }">&#x25CF;</span>
                                </div>
                                <span>{{ item.mapKeyLabel ? item.mapKeyLabel["enUS"] : item.label["enUS"] }}</span>
                            </div>
                            <div class="legend-item">
                                <div class="legend-item-icon">
                                    <span class="legend-dot" style="color: #ababab">&#x25CF;</span>
                                </div>
                                <span>{{ $t("map.legend.retired") }}</span>
                            </div>
                            <div class="legend-item">
                                <div class="legend-item-icon">
                                    <span class="legend-dot" style="color: #4c5563">&#x25CF;</span>
                                </div>
                                <span>{{ $t("map.legend.maintenance") }}</span>
                            </div>
                            <div class="legend-item legend-item-community">
                                <div class="legend-item-icon">
                                    <span class="legend-dot" style="background-color: #5c6bc0; margin-right: 10px"></span>
                                </div>
                                <span>{{ $t("map.legend.community") }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="container-map">
                    <template v-if="isPartnerCustomisationEnabled">
                        <StationsMap
                            @show-summary="showSummary"
                            :mapped="mappedProject"
                            :layoutChanges="layoutChanges"
                            :showStations="project.showStations"
                            :visibleReadings="visibleReadings"
                            :mapBounds="mapBounds"
                        />

                        <StationHoverSummary
                            v-if="activeStation"
                            :station="activeStation"
                            :sensorDataQuerier="sensorDataQuerier"
                            :exploreContext="exploreContext"
                            :visibleReadings="visibleReadings"
                            :hasCupertinoPane="true"
                            @close="onCloseSummary"
                            v-slot="{ sensorDataQuerier }"
                        >
                            <TinyChart
                                v-if="!isRetiredStation(activeStation)"
                                :station-id="activeStation.id"
                                :station="activeStation"
                                :querier="sensorDataQuerier"
                            />
                        </StationHoverSummary>
                    </template>

                    <template v-else>
                        <StationsMap
                            @show-summary="showSummary"
                            :mapped="mappedProject"
                            :layoutChanges="layoutChanges"
                            :showStations="project.showStations"
                            :visibleReadings="visibleReadings"
                            :mapBounds="mapBounds"
                            :showSidebar="true"
                        >
                            <StationsMapSummary
                                v-if="activeStation"
                                v-slot="{ sensorDataQuerier }"
                                :station="activeStation"
                                :sensorDataQuerier="sensorDataQuerier"
                                :exploreContext="exploreContext"
                                :visibleReadings="visibleReadings"
                                @close="onCloseSummary"
                            >
                                <TinyChart
                                    v-if="!isRetiredStation(activeStation)"
                                    :station-id="activeStation.id"
                                    :station="activeStation"
                                    :querier="sensorDataQuerier"
                                />
                            </StationsMapSummary>
                        </StationsMap>
                    </template>
                </div>
            </template>
        </div>

        <MapViewTypeToggle
            :routes="[
                { name: 'viewProjectBigMap', label: 'map.toggle.map', viewType: 'map', params: { id: id } },
                { name: 'viewProjectBigMapList', label: 'map.toggle.list', viewType: 'list', params: { id: id } },
            ]"
        ></MapViewTypeToggle>
    </StandardLayout>
</template>

<script lang="ts">
import _ from "lodash";
import * as utils from "../../utilities";
import { mapGetters, mapState } from "vuex";
import {
    ActionTypes,
    BoundingRectangle,
    DisplayStation,
    DisplayProject,
    GlobalState,
    MappedStations,
    Project,
    VisibleReadings,
} from "@/store";
import { SensorDataQuerier } from "@/views/shared/sensor_data_querier";

import Vue from "vue";
import StandardLayout from "../StandardLayout.vue";
import StationsMap from "../shared/StationsMap.vue";
import StationHoverSummary from "@/views/shared/StationHoverSummary.vue";
import StationsMapSummary from "@/views/shared/StationsMapSummary.vue";
import TinyChart from "@/views/viz/TinyChart.vue";
import CommonComponents from "@/views/shared";
import ProjectDetailCard from "@/views/projects/ProjectDetailCard.vue";
import { ExploreContext, VizThresholds } from "@/views/viz/common";

import { getPartnerCustomizationWithDefault, isCustomisationEnabled } from "@/views/shared/partners";
import MapViewTypeToggle from "@/views/shared/MapViewTypeToggle.vue";
import { MapViewType } from "@/api/api";
import { isRetiredStation } from "@/utilities/station-status";

export default Vue.extend({
    name: "ProjectBigMap",
    components: {
        ...CommonComponents,
        StationsMap,
        StationHoverSummary,
        StationsMapSummary,
        StandardLayout,
        ProjectDetailCard,
        TinyChart,
        MapViewTypeToggle,
    },
    data(): {
        layoutChanges: number;
        activeStationId: number | null;
        recentMapMode: boolean;
        legendCollapsed: boolean;
        isMobileView: boolean;
        showWinterBanner: boolean;
    } {
        return {
            layoutChanges: 0,
            activeStationId: null,
            recentMapMode: false,
            legendCollapsed: false,
            isMobileView: window.screen.availWidth <= 768,
            showWinterBanner: false,
        };
    },
    props: {
        id: {
            required: true,
            type: Number,
        },
    },
    computed: {
        ...mapGetters({ isAuthenticated: "isAuthenticated", isBusy: "isBusy" }),
        ...mapState({
            user: (s: GlobalState) => s.user.user,
            userStations: (s: GlobalState) => Object.values(s.stations.user.stations),
        }),
        displayProject(): DisplayProject {
            return this.$getters.projectsById[this.id];
        },
        visibleReadings(): VisibleReadings {
            return this.recentMapMode ? VisibleReadings.Last72h : VisibleReadings.Current;
        },
        project(): Project {
            return this.displayProject.project;
        },
        sensorDataQuerier(): SensorDataQuerier {
            return new SensorDataQuerier(this.$services.api);
        },
        projectStations(): DisplayStation[] {
            const stations = this.$getters.projectsById[this.id].stations;
            const sortFactors = _.fromPairs(stations.map((station) => [station.id, station.getSortOrder(this.visibleReadings)]));
            return _.orderBy(
                this.$getters.projectsById[this.id].stations.slice(),
                [(station) => sortFactors[station.id][0], (station) => sortFactors[station.id][1], (station) => sortFactors[station.id][2]],
                ["asc", "desc", "asc"]
            );
        },
        mappedProject(): MappedStations | null {
            return this.$getters.projectsById[this.id].mapped;
        },
        activeStation(): DisplayStation | null {
            if (this.activeStationId) {
                return this.$getters.stationsById[this.activeStationId];
            }
            return null;
        },
        projectModules(): { name: string; url: string }[] {
            return this.$getters.projectsById[this.displayProject.id].modules.map((m) => {
                return {
                    name: m.name,
                    url: this.$loadAsset(utils.getModuleImg(m)),
                };
            });
        },
        mapBounds(): BoundingRectangle {
            if (this.project.bounds?.min && this.project.bounds?.max) {
                return new BoundingRectangle(this.project.bounds?.min, this.project.bounds?.max);
            }

            return MappedStations.defaultBounds();
        },
        exploreContext(): ExploreContext {
            return new ExploreContext(this.project.id, true);
        },
        stationsWithData(): DisplayStation[] {
            return this.displayProject.stations.filter((station) => station.hasData);
        },
        hasStationsWithoutData(): boolean {
            return this.stationsWithData.length < this.projectStations.length;
        },
        keyTitle(): string {
            if (this.stationsWithData.length > 0) {
                return this.getThresholds(this.stationsWithData).label["enUS"];
            } else {
                return "";
            }
        },
        levels(): object[] {
            if (this.stationsWithData.length > 0) {
                return this.getThresholds(this.stationsWithData).levels.filter((d) => d["label"]);
            } else {
                return [];
            }
        },
        partnerCustomization() {
            return getPartnerCustomizationWithDefault();
        },
        viewType(): MapViewType {
            if (this.$route.meta?.viewType) {
                return this.$route.meta.viewType;
            }
            return MapViewType.map;
        },
        isPartnerCustomisationEnabled(): boolean {
            return isCustomisationEnabled();
        },
    },
    watch: {
        id(): Promise<any> {
            if (this.id) {
                return this.$store.dispatch(ActionTypes.NEED_PROJECT, { id: this.id });
            }
            return Promise.resolve();
        },
    },
    beforeMount(): Promise<any> {
        if (this.id) {
            return this.$store.dispatch(ActionTypes.NEED_PROJECT, { id: this.id }).then(() => {
                this.checkWinterBannerVisibility();
            });
        }
        return Promise.resolve();
    },
    methods: {
        isRetiredStation,
        async checkWinterBannerVisibility(): Promise<void> {
            if (!this.isPartnerCustomisationEnabled) {
                return;
            }

            const endDate = new Date("2026-03-15T23:59:59-05:00");
            const clientTime = new Date();
            this.showWinterBanner = clientTime <= endDate;
        },
        switchView(): void {
            this.activeStationId = null;
            this.$nextTick(() => {
                this.layoutChanges++;
            });
        },
        showSummary(station: DisplayStation): void {
            console.log("map: show-summay", station);
            this.activeStationId = station.id;
        },
        onCloseSummary(): void {
            this.activeStationId = null;
            this.$nextTick(() => {
                this.layoutChanges++;
                console.log("ProjectBigMap: Incrementing layoutChanges after closing summary");
            });
        },
        getThresholds(stations: DisplayStation[]): VizThresholds {
            try {
                const thresholds = stations.flatMap((station) => {
                    return station.configurations.all[0].modules[0].sensors.flatMap((sensor) => {
                        return sensor.meta.viz[0].thresholds;
                    });
                });

                if (thresholds.length > 0) {
                    return thresholds[0];
                }

                console.error("get-thresholds:none");

                return {
                    label: {},
                    levels: [],
                };
            } catch (error) {
                console.error("get-thresholds", error);
                return {
                    label: {},
                    levels: [],
                };
            }
        },
        sidebarToggle() {
            this.$nextTick(() => {
                this.layoutChanges++;
            });
        },
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/project";
@use "src/scss/global";
@use "src/scss/mixins";
@use "src/scss/variables";

.container-map {
    width: 100%;
    height: calc(100% - 157px);
    margin-top: 0;
    overflow: hidden;
    @include mixins.position(absolute, 157px null null 0);

    body:not(.floodnet) & {
        @include mixins.bp-down(variables.$md) {
            top: 151px;
        }
    }

    @include mixins.bp-down(variables.$sm) {
        top: 54px;
        height: calc(100% - 54px);
    }

    ::v-deep .stations-map {
        height: 100% !important;
    }

    ::v-deep .sidebar,
    .station-map-summary {
        height: 100%;
    }
}
.map-legend {
    display: flex;
    flex-direction: column;
    border: 1px solid #f4f5f7;
    border-radius: 3px;
    z-index: variables.$z-index-top;
    position: absolute;
    bottom: 80px;
    right: 0;
    box-sizing: border-box;
    background-color: #fcfcfc;
    text-align: left;
    font-family: variables.$font-family-medium;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.13);

    h4 {
        margin: 0 0 0.5em 0;
        font-family: variables.$font-family-bold;
    }

    &.collapsed {
        .legend-toggle {
            left: -25px;
        }

        .legend-content {
            max-width: 0;
        }

        .icon-chevron-right {
            transform: rotate(180deg) translateX(0);
        }
    }

    .icon-chevron-right {
        transform: translateX(2px);
    }

    .legend-content {
        overflow: hidden;
        white-space: nowrap;
        max-width: 310px;
        transition: all 0.25s ease-in-out;
    }

    .legend-content-wrap {
        padding: 15px 20px 15px 15px;
    }

    .legend-toggle {
        @include mixins.position(absolute, 11px null null -24px);
        @include mixins.flex(center, center);
        height: 35px;
        width: 24px;
        font-size: 22px;
        color: #979797;
        background-color: #fcfcfc;
        box-shadow: -1px 2px 2px 0 rgb(0 0 0 / 13%);
        cursor: pointer;
    }

    .legend-item {
        margin-bottom: 0.2em;
        display: flex;
        align-items: center;
    }
    .legend-dot {
        font-size: 1.5em;
    }
    .legend-item-icon {
        flex: 0 0 22px;
        transform: translateY(-2px);
    }
}

.legend-item-community {
    margin-top: 8px;

    .legend-item-icon {
        transform: translateY(-1px);
    }

    .legend-dot {
        display: inline-block;
        width: 12px;
        height: 12px;
        border-radius: 1px;
        margin-left: 2px;
        margin-right: 12px;
        width: 10px;
        height: 10px;
    }
}

::v-deep .station-hover-summary {
    width: 359px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.stations-list {
    @include mixins.flex();
    flex-wrap: wrap;
    padding: 160px 40px;
    width: 100%;
    box-sizing: border-box;

    @include mixins.bp-down(variables.$md) {
        padding: 100px 20px;
        margin: 30px -20px -20px;
    }

    @include mixins.bp-down(variables.$sm) {
        justify-content: center;
    }

    @include mixins.bp-down(variables.$xs) {
        padding: 80px 0px;
        margin: 55px 0 -5px 0;
        transform: translateX(10px);
        width: calc(100% - 20px);
    }

    ::v-deep .station-hover-summary {
        position: unset;
        transform: translate(0, 0) !important;
    }

    ::v-deep .summary-container {
        z-index: 0;
        position: unset;
        margin: 20px;
        flex-basis: 389px;
        box-sizing: border-box;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.07);

        @include mixins.bp-down(variables.$md) {
            padding: 19px 11px;
            flex-basis: calc(50% - 40px);
        }

        @include mixins.bp-down(variables.$sm) {
            justify-self: center;
            flex: 1 1 389px;
            max-width: 389px;
            margin: 10px 0;
        }

        @include mixins.bp-down(variables.$xs) {
            margin: 5px 0;
            width: auto;
        }

        .close-button {
            display: none;
        }

        .navigate-button {
            right: 0;
        }
    }
}

::v-deep .mapboxgl-ctrl-geocoder {
    margin: 30px 0 0 30px;

    @include mixins.bp-down(variables.$sm) {
        margin: 61px 0 0 10px;
    }
}

.winter-banner-visible ::v-deep .mapboxgl-ctrl-geocoder {
    margin-top: 82px;

    @include mixins.bp-down(variables.$sm) {
        margin-top: 117px;
    }
}

::v-deep .mapboxgl-ctrl-bottom-left {
    margin-left: 25px;
}

.winter-banner-visible ~ ::v-deep .view-type-container {
    @include mixins.bp-down(variables.$sm) {
        top: 172px !important;
        right: 10px !important;
    }
}

.floodnet-winter-banner {
    background-color: #fff;
    position: absolute;
    top: calc(66px + 101px);
    left: 0;
    z-index: variables.$z-index-top;
    box-sizing: border-box;
    width: 100%;
    padding: 12px 30px;
    font-size: 16px;
    font-family: var(--font-family-bold);
    text-align: left;

    @include mixins.bp-down(variables.$sm) {
        top: calc(66px + 38px);
        font-size: 14px;
        padding: 12px 10px;
    }
}
</style>
